import { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import type { TabItem } from '.';
import type { CTAProps, Customization, TabContentComponentProps } from '.';
import styles from './index.module.less';
import { Column, type ColumnSize } from '../../atoms/grid';
import { Color } from '../../../core/enums';
import withEventTracking from '../../../utilities/withEventTracking';
import Tab from '../../archived/tab';
import Element from '../../../core/Element';

const positionColorMap = {
  top: {
    [Color.DeepBlue]: 'activeDeepBlueTop',
    [Color.BrightBlue]: 'activeBrightBlueTop',
    [Color.Gray06]: 'activeGray06Top',
  },
  bottom: {
    [Color.DeepBlue]: 'activeDeepBlueBottom',
    [Color.BrightBlue]: 'activeBrightBlueBottom',
    [Color.Gray06]: 'activeGray06Bottom',
  },
};

const CTAWithTracking = withEventTracking<CTAProps>(
  ({
    children,
    onClick,
    accentBarColor = Color.BrightBlue,
    accentBarPosition = 'top',
    selected,
    tabPanelIndex,
    id,
    tabsRef,
    qeId,
  }: CTAProps) => {
    const accentBarStyle = positionColorMap[accentBarPosition][accentBarColor];

    return (
      <Tab
        key={id}
        id={id}
        Component="button"
        onClick={onClick}
        className={classnames(styles.tab, {
          [styles[accentBarStyle]]: selected,
        })}
        aria-selected={selected}
        aria-controls={`tabpanel-${qeId}-${tabPanelIndex}`}
        tabIndex={selected ? '0' : '-1'}
        ref={(el: HTMLButtonElement) => (tabsRef.current[tabPanelIndex] = el)}
      >
        {children}
      </Tab>
    );
  }
);

const TabContentComponent = ({ content }: TabContentComponentProps) => (
  <Element theme={{ marginTop: 10 }}>{content}</Element>
);

interface Props {
  headerId?: string;
  tabItems: TabItem[];
  activeIndex: number;
  onIndexChange?: (tabState: TabContentComponentProps, index: number) => void;
  autoActivation?: boolean;
  customization?: Customization;
  qeId: string;
  styles: { [index: string]: string };
  isSmall: boolean;
  shouldStayHorizontal: boolean;
}

const defaultCustomizations: Customization = {
  $accentBarPosition: 'top',
  accentBarColor: Color.DeepBlue,
};

export const TabGroup = ({
  tabItems,
  onIndexChange = () => {},
  autoActivation,
  headerId,
  isSmall,
  shouldStayHorizontal,
  activeIndex = 0,
  customization = defaultCustomizations,
  qeId = 'TabGroup',
}: Props): React.ReactElement => {
  const [currentIndex, setCurrentIndex] = useState<number>(activeIndex);
  const [focusIndex, setFocusIndex] = useState<number>(activeIndex);

  useEffect(() => {
    setCurrentIndex(activeIndex);
  }, [activeIndex]);

  const tabsRef = useRef<HTMLButtonElement[]>([]);

  const handleNextTab = (
    firstTabInRound: number,
    nextTab: number,
    lastTabInRound: number
  ) => {
    const tabToSelect =
      focusIndex === lastTabInRound ? firstTabInRound : nextTab;

    if (!autoActivation) {
      setFocusIndex(tabToSelect);
      tabsRef.current[tabToSelect].focus();
      return;
    }
    onIndexChange(tabItems[tabToSelect], tabToSelect);
    setCurrentIndex(tabToSelect);
    setFocusIndex(tabToSelect);
    tabsRef.current[tabToSelect].focus();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    const tabCount = Object.keys(tabItems).length - 1;

    if (event.key === 'ArrowLeft') {
      const last = tabCount;
      const next = focusIndex - 1;
      handleNextTab(last, next, 0);
      event.stopPropagation();
      event.preventDefault();
    }
    if (event.key === 'ArrowRight') {
      const first = 0;
      const next = focusIndex + 1;
      handleNextTab(first, next, tabCount);
      event.stopPropagation();
      event.preventDefault();
    }
  };
  const breakToVertical = isSmall && !shouldStayHorizontal;
  const columnSize = breakToVertical ? '0' : null;
  const size: ColumnSize =
    columnSize || (`${Math.floor(12 / tabItems.length)}` as ColumnSize);
  const { $accentBarPosition, accentBarColor } = {
    ...defaultCustomizations,
    ...customization,
  };

  return (
    <Element
      role="tablist"
      {...(headerId ? { 'aria-labelledby': headerId } : {})}
      aria-orientation={breakToVertical ? 'vertical' : 'horizontal'}
      qeId={qeId}
      className={classnames(styles.tabsWrapper, {
        [styles.breakToVertical]: breakToVertical,
      })}
      onKeyDown={handleKeyPress}
    >
      {tabItems.map(
        (
          {
            tabId,
            content,
            Component = TabContentComponent,
            trackingElement,
            ...componentProps
          },
          i
        ) => {
          const isActive = currentIndex === i;
          return (
            <Column
              key={tabId}
              $sm={size}
              theme={{
                padding: 0,
              }}
            >
              <CTAWithTracking
                selected={isActive}
                id={tabId}
                qeId={qeId}
                tabPanelIndex={i}
                theme={{
                  padding: 10,
                  bgColor: isActive ? Color.White : Color.Gray01,
                  borderColor: isActive ? Color.White : Color.Gray02,
                  focusDark: true,
                }}
                accentBarColor={accentBarColor}
                accentBarPosition={$accentBarPosition}
                onClick={() => {
                  if (!isActive) {
                    setCurrentIndex(i);
                    setFocusIndex(i);
                    onIndexChange(tabItems[i], i);
                  }
                }}
                tools={{
                  tracking: {
                    methods: ['click'],
                    element: trackingElement || `${i}`,
                  },
                }}
                tabsRef={tabsRef}
              >
                <Component
                  isActive={isActive}
                  content={content}
                  {...componentProps}
                />
              </CTAWithTracking>
            </Column>
          );
        }
      )}
    </Element>
  );
};
